import React, { useReducer, useState, Dispatch } from 'react';
import { Box, Tabs, Tab, Stack, IconButton } from '@mui/material';
import { classes } from '../styles';
import StyledSnackbar from '../../Components/CustomUIElements/StyledSnackbar';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import Analytics from './Analytics/Analytics';
import Recipients from './Recipients/Recipients';
import EmailContent from './EmailContent/EmailContent';
import { GenericTableAction } from '../../Components/GenericTable/genericTableReducer';
import { TEmail, RecipientsAPIReturn } from '../types';
import { ArrowCircleLeft as ArrowCircleLeftIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import callAPI from '../../Components/Utilities/callAPI';
import { getStatusColor } from '../helper';
import {
  genericTableReducer,
  initialTableState
} from '../../Components/GenericTable/genericTableReducer';
import { recipientColumns } from './Recipients/config';
import { callPaginatedAPI } from '../../Components/Utilities/callAPI';
import queryString from 'query-string';
import { CandidateSourcingAction } from '../candidateSourcingReducer';

const CandidateSourcingEmailAnalytics = ({
  selectedEmail,
  emailsDispatch,
  selectedPoolId
}: {
  selectedEmail: TEmail;
  emailsDispatch: Dispatch<CandidateSourcingAction>;
  selectedPoolId: number;
}) => {
  const [modalTabValue, setModalTabValue] = useState<number>(0);
  const [snackbarState, setSnackbarState] = useState({
    message: '',
    state: 'success' as 'success' | 'error' | 'warning'
  });
  const [recipientsState, recipientsDispatch] = useReducer(
    genericTableReducer,
    initialTableState(recipientColumns, { sortBy: 'name', sortOrder: 'asc' })
  );

  const { data: emailAnalytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['candidateSourcingEmailAnalytics', selectedEmail.item.id],
    queryFn: () =>
      callAPI(
        `/api/v4/candidate_sourcing/candidate_pools/${selectedPoolId}/email_batches/${selectedEmail.item.id}/analytics`,
        null,
        'GET'
      ),
    onError: () =>
      setSnackbarState({
        message: `There was an error getting email analytics data`,
        state: 'error'
      }),
    refetchOnWindowFocus: false
  });

  const { data: emailContent, isLoading: contentLoading } = useQuery({
    queryKey: ['candidateSourcingEmailContent', selectedEmail.item.id],
    queryFn: () =>
      callAPI(
        `/api/v4/candidate_sourcing/candidate_pools/${selectedPoolId}/email_batches/${selectedEmail.item.id}/content`,
        null,
        'GET'
      ),
    onError: () =>
      setSnackbarState({
        message: `There was an error getting email content data`,
        state: 'error'
      }),
    refetchOnWindowFocus: false
  });

  const { data: recipients, isLoading: fetchingRecipients } = useQuery({
    queryKey: ['recipientsAnalytics', recipientsState.searchQuery, recipientsState.searchSettings],
    queryFn: () => {
      return callPaginatedAPI(
        `/api/v4/candidate_sourcing/candidate_pools/${selectedPoolId}/email_batches/${
          selectedEmail.item.id
        }/recipients?${queryString.stringify(
          {
            ...recipientsState.searchSettings,
            'q[search]': recipientsState.searchQuery
          },
          {
            skipEmptyString: true,
            skipNull: true,
            arrayFormat: 'bracket',
            encode: false
          }
        )}`,
        null,
        'GET'
      ) as Promise<RecipientsAPIReturn>;
    },
    onError() {
      setSnackbarState({
        message: `There was an error retrieving recipients`,
        state: 'error'
      });
    },
    refetchOnWindowFocus: false
  });

  return (
    <>
      <Box>
        <Box sx={{ ...classes.pageHeader, paddingLeft: '0' }}>
          <h1>{selectedEmail.item.email_template}</h1>
        </Box>
        <Box
          sx={{
            backgroundColor: getStatusColor(selectedEmail.item.status)[1],
            padding: '0.4rem 2.5rem',
            width: 'fit-content'
          }}
        >
          <Box
            sx={{
              color: getStatusColor(selectedEmail.item.status)[0],
              fontWeight: 'bold',
              fontSize: '18px'
            }}
          >
            {selectedEmail.item.status.toUpperCase()}
          </Box>
        </Box>
        <Tabs
          value={modalTabValue}
          onChange={(e, value) => setModalTabValue(value)}
          TabIndicatorProps={{
            sx: { maxWidth: modalTabValue === 2 ? '8.5em' : modalTabValue ? '7em' : '5em' }
          }}
          sx={{
            ...classes.tabs,
            '& .MuiTabs-indicator': {
              ...classes.tabIndicator
            },
            margin: '1rem 0 1.5rem 0',
            borderBottom: '1px solid #EEEEEE'
          }}
        >
          <Tab label="Analytics" id="analytics-tab" />
          <Tab label="Recipients" id="recipients-tab" />
          <Tab label="Email Content" id="email-content-tab" />
        </Tabs>
        <Box
          sx={{ ...sharedClasses.navigateBackButton, marginBottom: '40px', marginLeft: '-10px' }}
        >
          <Stack
            flexDirection="row"
            alignItems="center"
            onClick={() => emailsDispatch({ type: 'CLEAR_SELECTED_EMAIL_FOR_ANALYTICS' })}
          >
            <IconButton sx={{ color: '#666666', fontSize: '28px' }}>
              <ArrowCircleLeftIcon />
            </IconButton>
            <Box id="back-to-pool-button">Back</Box>
          </Stack>
        </Box>
        <Box>
          {modalTabValue === 0 && (
            <Analytics
              selectedEmail={selectedEmail}
              emailAnalytics={emailAnalytics}
              isLoading={analyticsLoading}
            />
          )}
        </Box>
        {modalTabValue === 1 && (
          <Recipients
            isFetching={fetchingRecipients}
            recipients={recipients}
            recipientsState={recipientsState}
            recipientsDispatch={recipientsDispatch}
          />
        )}
        {modalTabValue === 2 && (
          <EmailContent isLoading={contentLoading} emailContent={emailContent} />
        )}
      </Box>
      <StyledSnackbar
        message={snackbarState.message}
        state={snackbarState.state}
        setSnackbarState={setSnackbarState}
      />
    </>
  );
};

export default CandidateSourcingEmailAnalytics;
